import { EnvelopeSimpleIcon, InfoIcon, WarningIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { blockedReasonKey, useNegotiationPreview } from '@entities/negotiation';
import type { SelectionSlot } from '@entities/selection';
import { Button, Checkbox, Skeleton } from '@shared/ui';

import styles from './OutreachForm.module.css';

interface OutreachFormProps {
  slots: SelectionSlot[];
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * «Эти четыре подходят»: поручение Karl провести проверку (FE-170, MVP_PRD §21.5, §36).
 *
 * С 09.09.2026 это единственное подтверждение проверки: дальше Karl сам
 * задаёт автохаусам вопросы, дописывает оставшиеся и уточняет общие ответы —
 * без правки состава вопросов, без текста письма и без окна отмены. Экран
 * говорит, **что** произойдёт, и просит одно согласие.
 *
 * Четыре правила экрана:
 *
 * * **ни одного поля цены и условий.** Karl на этом этапе только спрашивает,
 *   и сказано это прямо: цены появляются на экране мандата (§21.5, «Экран 1»);
 * * **неактивное состояние всегда с причиной.** «Объявление больше не
 *   активно», «У продавца не указан адрес для обращений», «Продавец отказался
 *   от переписки» — общего «недоступно» не бывает (§21.2);
 * * **состав закрывается здесь.** Замен после подтверждения не будет — это
 *   сказано в согласии, а не выясняется после нажатия (§34.5);
 * * **кнопка неактивна без чекбокса.** Karl не пишет автохаусам без явного
 *   поручения — это граница продукта, а не настройка.
 *
 * Вводный абзац («ответы цитируются дословно, CarViQ.DE их не проверяет»)
 * стоит над формой репликой Karl (`KarlIntro` на экране, DESIGN_SYSTEM §7.2).
 * Запуск проверки — **единственная красная кнопка** экрана: он необратим и
 * идёт в два шага — отметка поручения, потом кнопка, без оптимистичного UI.
 */
export function OutreachForm({ slots, busy, error, onConfirm, onCancel }: OutreachFormProps) {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);

  return (
    <div className={styles.wrap}>
      {/* Прямая формулировка §21.5: «Karl только задаёт вопросы». */}
      <p className={styles.noPrices}>
        <InfoIcon size={20} aria-hidden="true" className={styles.noPricesIcon} />
        <span>{t('negotiation.outreach.noPrices')}</span>
      </p>

      <ul className={styles.cards}>
        {slots.map((slot) => (
          <li key={slot.selection_id}>
            <SlotPreview slot={slot} />
          </li>
        ))}
      </ul>

      {error === null ? null : (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <Checkbox
        checked={accepted}
        onChange={(event) => {
          setAccepted(event.target.checked);
        }}
      >
        {t('negotiation.outreach.consent')}
      </Checkbox>

      <div className={styles.actions}>
        <Button variant="secondary" disabled={busy} onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button disabled={busy || !accepted} onClick={onConfirm}>
          {busy ? t('negotiation.outreach.pending') : t('negotiation.outreach.confirm')}
        </Button>
      </div>

      {accepted ? null : <p className={styles.hint}>{t('negotiation.outreach.consentRequired')}</p>}
    </div>
  );
}

/**
 * Карточка одного автомобиля четвёрки с предусловиями.
 *
 * Предусловия запрашиваются **по каждому автомобилю отдельно**: они у всех
 * разные, и один общий ответ «всё хорошо» скрыл бы автомобиль, по которому
 * написать нельзя. Текст письма и список вопросов не показываются: их
 * определяет backend, и пользователь их не правит.
 */
function SlotPreview({ slot }: { slot: SelectionSlot }) {
  const { t } = useTranslation();
  const preview = useNegotiationPreview(slot.vehicle.vehicle_id);

  return (
    <article className={styles.card}>
      <h3 className={styles.cardTitle}>
        {slot.vehicle.make} {slot.vehicle.model}
        {slot.vehicle.variant === null ? null : ` · ${slot.vehicle.variant}`}
      </h3>
      <p className={styles.seller}>
        {slot.vehicle.seller_name ?? t('negotiation.list.unknownSeller')}
        {slot.vehicle.seller_city === null ? null : ` · ${slot.vehicle.seller_city}`}
      </p>

      {preview.isPending ? <Skeleton height="1.5rem" /> : null}

      {/* Состояние — иконка плюс текст, цвет не единственный носитель
          смысла (DESIGN_SYSTEM §2.4). «Можно написать» — нейтральная
          подпись, а не зелёная: это не «соответствует». */}
      {preview.data === undefined ? null : preview.data.can_start ? (
        <p className={styles.ready}>
          <EnvelopeSimpleIcon size={16} aria-hidden="true" className={styles.stateIcon} />
          <span>{t('negotiation.outreach.ready')}</span>
        </p>
      ) : (
        <p className={styles.blocked} role="note">
          <WarningIcon size={16} weight="fill" aria-hidden="true" className={styles.stateIcon} />
          <span>{t(blockedReasonKey(preview.data.blocked_reason ?? 'UNKNOWN'))}</span>
        </p>
      )}
    </article>
  );
}
